import React from "react";
import { motion } from "framer-motion";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Rating from "@mui/material/Rating";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addToCart } from "@/store/slices/cartSlice";
import { useProducts } from "@/hooks/useProducts";
import { useCategories } from "@/hooks/useCategories";
import ImageWithFallback from "@/components/ImageWithFallback";
import LoadingScreen from "@/components/LoadingScreen";
import { getOfferCountdownSetting } from "@/api/settings";


const useCountdown = (targetDateStr, isActive) => {
  const [remainingMs, setRemainingMs] = React.useState(0);

  React.useEffect(() => {
    if (!isActive || !targetDateStr) {
      setRemainingMs(0);
      return;
    }

    const targetTime = new Date(targetDateStr).getTime();
    
    const updateTime = () => {
      const diff = targetTime - Date.now();
      setRemainingMs(Math.max(0, diff));
    };
    
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, [targetDateStr, isActive]);

  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const isExpired = remainingMs <= 0;
  return { days, hours, minutes, seconds, isExpired };
};

const normalizeCategoryName = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/\s+/g, " ")
    .trim();

const FeaturedProductCard = ({ product, isLarge }) => (
  <div className="bg-white rounded-2xl p-6 shadow flex flex-col text-left">
    <div className="flex gap-4 items-center">
      <ImageWithFallback
        src={product.images?.[0]}
        alt={product.name}
        className={`${isLarge ? 'w-36 h-36' : 'w-24 h-24'} rounded-xl object-cover`}
      />
      <div className="flex-1">
        <div className="font-medium">{product.name}</div>
        {Number(product.avgRating) > 0 ? (
          <div className="flex items-center gap-1 mt-1">
            <Rating value={Number(product.avgRating)} readOnly size="small" precision={0.1} sx={{ color: '#fbbf24' }} />
            <span className="text-xs text-slate-400 font-bold">({product.reviewCount || 0})</span>
          </div>
        ) : (
          <div className="text-slate-600 text-sm mt-1">
            Pure and natural from our trusted farms
          </div>
        )}
        <div className="mt-3 flex items-center justify-between">
          <div className="text-slate-900 font-semibold">
            {product.discount > 0 ? (
              <div className="flex flex-col">
                <span className="font-bold">₹{(product.price * (1 - product.discount / 100)).toFixed(0)}</span>
                <span className="text-xs text-slate-400 line-through">₹{product.price}</span>
              </div>
            ) : (
              <span>₹{product.price}</span>
            )}
          </div>
          <Button
            variant="contained"
            size={isLarge ? "medium" : "small"}
            component={Link}
            to={`/product/${product._id}`}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            View
          </Button>
        </div>
      </div>
    </div>
  </div>
);

const Landing = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // API hooks
  const {
    categories: fetchedCategories,
    loading: categoriesLoading,
    error: categoriesError,
    hasMore: hasMoreCategories,
  } = useCategories();

  const [displayedCategories, setDisplayedCategories] = React.useState([]);

  React.useEffect(() => {
    if (fetchedCategories.length > 0) {
      setDisplayedCategories(fetchedCategories);
    }
  }, [fetchedCategories]);

  const { products: allProducts, loading, error } = useProducts({ limit: 20 });
  const { products: offerProducts } = useProducts({ discounted: true, limit: 10 });

  // Derive hero slides from allProducts
  const heroSlides = React.useMemo(() => {
    return allProducts
      .filter((p) => p.images && p.images.length > 0) // Filter products with images
      .slice(0, 4); // Take up to 4 products
  }, [allProducts]);

  // Calculate overall rating stats
  const { overallAvgRating, totalReviewCount } = React.useMemo(() => {
    const productsWithReviews = allProducts.filter(p => Number(p.reviewCount) > 0);
    if (productsWithReviews.length === 0) return { overallAvgRating: "4.9", totalReviewCount: 0 };
    
    let totalScore = 0;
    let totalCount = 0;
    productsWithReviews.forEach(p => {
      totalScore += Number(p.avgRating) * Number(p.reviewCount);
      totalCount += Number(p.reviewCount);
    });
    
    const avg = totalCount > 0 ? (totalScore / totalCount).toFixed(1) : "4.9";
    return { overallAvgRating: avg, totalReviewCount: totalCount };
  }, [allProducts]);

  const [activeTab, setActiveTab] = React.useState(0);
  const [offerSetting, setOfferSetting] = React.useState({ endTime: "", isActive: false, label: "" });

  React.useEffect(() => {
    const fetchOfferCountdown = async () => {
      try {
        const setting = await getOfferCountdownSetting();
        setOfferSetting(setting);
      } catch (err) {
        console.error("Failed to fetch offer settings:", err);
      }
    };
    fetchOfferCountdown();
  }, []);

  const { days, hours, minutes, seconds, isExpired } = useCountdown(
    offerSetting.endTime,
    offerSetting.isActive
  );

  const showCountdown = offerSetting.isActive && !isExpired;

  // Infinite scroll for categories
  const lastCategoryRef = React.useCallback(
    (node) => {
      // Intersection observer disabled as we now load all categories at once
    },
    []
  );

  const { selectFeatured, topSelling, featuredList, featuredTopProduct } = React.useMemo(() => {
    const selectFeatured = (offerProducts || []).filter(
      (p) => p && Number(p.discount) > 0
    );
    const topSelling = allProducts
      .filter((p) => ["Backpacks", "Headphones", "Bags"].includes(p.category))
      .slice(0, 8);

    // Get top rated products for the Featured section
    const topRated = [...allProducts]
      .filter(p => Number(p.avgRating) > 0)
      .sort((a, b) => Number(b.avgRating) - Number(a.avgRating));
      
    const featuredTopProduct = topRated.length > 0 ? topRated[0] : allProducts[4];
    const featuredList = topRated.length > 1 ? topRated.slice(1, 4) : allProducts.slice(10, 13);

    return { selectFeatured, topSelling, featuredList, featuredTopProduct };
  }, [allProducts, offerProducts]);

  if (loading) return <LoadingScreen />;
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
        <div className="text-center max-w-md mx-auto">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Failed to load products
          </h2>
          <p className="text-slate-600 mb-6">
            We're having trouble loading the featured products. Please try
            again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen text-slate-900">
      {/* Hero stays the same as before */}
      <section className="relative overflow-hidden">
        <ImageWithFallback
          src="https://images.unsplash.com/photo-1526470608268-f674ce90ebd4?q=80&w=2048&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
          alt="Hero Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/70 to-white/90 backdrop-blur" />
        <div className="relative container max-w-7xl mx-auto py-10 md:py-16 grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
          <div className="md:mx-auto md:max-w-xl text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/60 backdrop-blur text-slate-700 text-sm shadow">
              Authentic farm-fresh products, personally selected for quality you can trust.
            </div>
            <h1 className="font-display text-5xl md:text-6xl mt-4 leading-[1.1]">
              Handpicked Freshness, Straight From Trusted Farms
            </h1>
            <p className="mt-4 text-slate-700   rounded-xl inline-block px-3 py-2">
              At Kidilan.com, we focus on bringing you authentic, farm-fresh products that are personally chosen for their superior quality. Every item is carefully handpicked to ensure freshness, taste, and trust in every purchase. We believe good products should come from genuine sources, and we are committed to offering only the best to our customers.
            </p>

            <div className="mt-6 flex items-center flex-wrap justify-center md:justify-start gap-3">
              <div className="flex -space-x-2">
                <img
                  className="w-8 h-8 rounded-full border border-white"
                  src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=80&q=80&auto=format&fit=crop"
                  alt="user1"
                />
                <img
                  className="w-8 h-8 rounded-full border border-white"
                  src="https://images.unsplash.com/photo-1547425260-76bcadfb4f2c?w=80&q=80&auto=format&fit=crop"
                  alt="user2"
                />
                <img
                  className="w-8 h-8 rounded-full border border-white"
                  src="https://images.unsplash.com/photo-1544723795-3fb6469f5b39?w=80&q=80&auto=format&fit=crop"
                  alt="user3"
                />
                <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-sm border border-white">
                  +5
                </div>
              </div>
              <div className="text-slate-700 text-sm">
                <span className="font-semibold text-slate-900">{overallAvgRating}</span>{" "}
                Ratings • {totalReviewCount > 0 ? `Based on ${totalReviewCount}+ verified reviews` : "Trusted by 1k+ customers"}
              </div>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
              <Link to="/shop">
                <Button variant="contained" color="primary">
                  Shop Now
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative md:mx-auto w-full">
            <div className="absolute -top-16 -left-16 w-64 h-64 bg-white/30 rounded-full blur-3xl" />
            <div className="absolute -bottom-10 -right-10 w-72 h-72 bg-white/30 rounded-full blur-3xl" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {heroSlides.map((item, i) => {
                if (!item) return null;
                return (
                  <div
                    key={item._id}
                    className="rounded-2xl shadow border border-white/50 p-3 bg-white/60 backdrop-blur supports-[backdrop-filter]:bg-white/40"
                  >
                    <Link to={`/product/${item._id}`} className="block">
                      {" "}
                      {/* Wrap with Link to PDP */}
                      <div className="relative overflow-hidden rounded-xl aspect-[4/3]">
                        <ImageWithFallback
                          src={item.images?.[0]} // Use first image from images array
                          alt={item.name}
                          className="w-full h-full object-cover transition duration-500 hover:scale-105"
                        />
                        <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/90 text-slate-800 text-xs shadow flex flex-col items-end">
                          {item.discount > 0 ? (
                            <>
                              <span className="font-bold">₹{(item.price * (1 - item.discount / 100)).toFixed(0)}</span>
                              <span className="text-[10px] text-slate-400 line-through">₹{item.price}</span>
                            </>
                          ) : (
                            <span>₹{item.price}</span>
                          )}
                        </div>
                      </div>
                    </Link>
                    <div className="mt-3 flex items-center justify-between">
                      <div>
                        <div className="font-medium">{item.name}</div>
                        <div className="text-slate-600 text-sm">
                          {item.subheading || "Authentic Farm Fresh"}
                        </div>
                      </div>
                      <button
                        aria-label="Add to cart"
                        onClick={() =>
                          dispatch(
                            addToCart({
                              productId: item._id,
                              name: item.name,
                              price: item.discount > 0 ? Math.round(item.price * (1 - item.discount / 100)) : item.price,
                              image: item.images?.[0],
                            })
                          )
                        }
                        className="h-9 w-9 rounded-full bg-slate-900 text-white grid place-items-center hover:opacity-90"
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Remaining sections use allProducts derived lists */}
      <section className="container max-w-7xl mx-auto py-8 text-center">
        <h2 className="font-display text-3xl mb-6">Explore Categories</h2>
        {categoriesError ? (
          <div className="text-red-500">Failed to load categories.</div>
        ) : (
          <div className="flex overflow-x-auto gap-4 py-2 scrollbar-hide">
            {displayedCategories.map((cat, index) => (
              <Link
                to={`/shop?categoryId=${encodeURIComponent(cat._id)}`}
                key={cat._id}
                className="group relative overflow-hidden rounded-2xl shadow block flex-shrink-0 w-48 md:w-64"
                ref={
                  index === displayedCategories.length - 1 && hasMoreCategories
                    ? lastCategoryRef
                    : null
                }
              >
                <ImageWithFallback
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-40 md:h-56 object-cover group-hover:scale-105 transition duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 font-semibold text-white drop-shadow">
                  {cat.name}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {selectFeatured && selectFeatured.length > 0 && (
        <section className="container max-w-7xl mx-auto py-8 text-center">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <h2 className="font-display text-3xl">Top Deals Of The Day</h2>
            {showCountdown && (
              <div className="text-sm text-slate-600 animate-pulse">
                {offerSetting.label || "Hurry up! Offer ends in:"} {days}d {hours}h {minutes}m {seconds}s
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <div className="flex gap-6 min-w-max justify-center">
              {selectFeatured.map((p) => (
                <Link
                  to={`/product/${p._id}`}
                  key={p._id}
                  className="w-64 shrink-0 bg-white rounded-2xl p-3 transition shadow hover:-translate-y-1 text-center"
                >
                  <div className="relative aspect-[4/3] overflow-hidden rounded-xl">
                    <ImageWithFallback
                      src={p.images?.[0]}
                      alt={p.name}
                      className="w-full h-full object-cover hover:scale-105 transition duration-500"
                    />
                  </div>
                  <div className="mt-3">
                    <div className="text-slate-700 text-sm mb-1">{p.name}</div>
                    <div className="flex items-center justify-center gap-2">
                      {p.discount > 0 ? (
                        <>
                          <span className="text-slate-900 font-bold">₹{(p.price * (1 - p.discount / 100)).toFixed(0)}</span>
                          <span className="text-slate-400 text-xs line-through">₹{p.price}</span>
                          <span className="text-green-600 text-[10px] font-bold">-{p.discount}%</span>
                        </>
                      ) : (
                        <span className="text-slate-900 font-semibold">₹{p.price}</span>
                      )}
                    </div>
                    {p.avgRating > 0 && (
                      <div className="flex items-center justify-center gap-1 mt-1">
                        <Rating value={Number(p.avgRating)} readOnly size="small" precision={0.1} sx={{ color: '#fbbf24' }} />
                        <span className="text-[10px] text-slate-400 font-bold">({p.reviewCount})</span>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      
      {/* Brand Story section with new background image */}
      <section id="story" className="relative py-20">
        <ImageWithFallback
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=2000&auto=format&fit=crop"
          alt="Brand Story"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative container max-w-7xl mx-auto">
          <div className="glass rounded-2xl p-8 md:p-12 text-center md:text-left">
            <h2 className="font-display text-white text-3xl mb-2">
              Authentic Products for Your Journey and Everyday Life
            </h2>
            <p className="text-white ">
              Whether for personal use, home use, or to take along when traveling
              abroad to the Gulf, Middle East, Europe, UK, USA, Canada, and more,
              we bring authentic Indian products you can trust. Soon, we will
              also introduce selected products from international countries —
              making quality products easier to access here in India through
              Kidilan.com.
            </p>
            {/* <div className="mt-4">
              <Button variant="contained" component={Link} to="/stories">
                Read the Story
              </Button>
            </div> */}
          </div>
        </div>
      </section>

      {/* Featured products highlight */}
      <section className="container max-w-7xl mx-auto py-8 text-center">
        <h2 className="font-display text-3xl mb-4">Top Featured Products</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {featuredTopProduct && (
            <FeaturedProductCard key={featuredTopProduct._id} product={featuredTopProduct} isLarge={true} />
          )}

          {featuredList.map((p) => (
            <FeaturedProductCard key={p._id} product={p} isLarge={false} />
          ))}
        </div>
      </section>

      {/* CTA and Newsletter remain as previously defined */}
      <section className="container max-w-7xl mx-auto py-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
        {[
          {
            title: "Special Offers",
            desc: "Save big on our exclusive handpicked deals",
            onClick: () => navigate("/shop?offer=1")
          },
          {
            title: "Food & Drink",
            desc: "Premium quality food and refreshing drinks",
            onClick: () => {
              const cat = fetchedCategories.find(
                (c) => normalizeCategoryName(c.name) === "food and drink"
              );
              if (cat?._id) navigate(`/shop?categoryId=${encodeURIComponent(cat._id)}`);
              else navigate("/shop");
            }
          },
          {
            title: "Whole Spices",
            desc: "Authentic, aromatic, and pure whole spices",
            onClick: () => {
              const cat = fetchedCategories.find(
                (c) => normalizeCategoryName(c.name) === "whole spices"
              );
              if (cat?._id) navigate(`/shop?categoryId=${encodeURIComponent(cat._id)}`);
              else navigate("/shop");
            }
          },
        ].map((c, i) => (
          <div
            key={i}
            className="rounded-2xl bg-slate-50 p-8 border border-slate-100"
          >
            <div className="font-display text-2xl">{c.title}</div>
            <div className="text-slate-600 mt-1 mb-2">{c.desc}</div>
            <Button
              variant="outlined"
              onClick={c.onClick}
              className="mt-12"
            >
              Shop Now
            </Button>
          </div>
        ))}
      </section>
    </div>
  );
};

export default Landing;
